import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Play,
  Pause,
  Eye,
  Copy,
  Calendar
} from 'lucide-react';
import CampaignModal from '../components/CampaignModal';

const Campaigns = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Données simulées
  const [campaigns, setCampaigns] = useState([
    {
      id: 1,
      nom: 'Promotion Été 2024',
      message: 'Découvrez nos offres d\'été avec -30% sur tous les produits !',
      type_envoi: 'immédiat',
      statut: 'terminé',
      date_creation: '2024-06-15',
      date_planifiee: null,
      total_envoyes: 1250,
      total_livres: 1234,
      taux_livraison: 98.7
    },
    {
      id: 2,
      nom: 'Rappel RDV',
      message: 'N\'oubliez pas votre rendez-vous demain à 14h.',
      type_envoi: 'planifié',
      statut: 'en_cours',
      date_creation: '2024-06-16',
      date_planifiee: '2024-06-20T10:00:00',
      total_envoyes: 890,
      total_livres: 876,
      taux_livraison: 98.4
    },
    {
      id: 3,
      nom: 'Newsletter Mensuelle',
      message: 'Découvrez les dernières actualités de notre entreprise.',
      type_envoi: 'planifié',
      statut: 'brouillon',
      date_creation: '2024-06-18',
      date_planifiee: '2024-06-25T09:00:00',
      total_envoyes: 0,
      total_livres: 0,
      taux_livraison: 0
    },
  ]);

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.statut === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'terminé': return 'text-green-600 bg-green-100';
      case 'en_cours': return 'text-blue-600 bg-blue-100';
      case 'brouillon': return 'text-yellow-600 bg-yellow-100';
      case 'annulé': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'terminé': return 'Terminé';
      case 'en_cours': return 'En cours';
      case 'brouillon': return 'Brouillon';
      case 'annulé': return 'Annulé';
      default: return status;
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setIsModalOpen(true);
  };

  const handleDelete = (campaignId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette campagne ?')) {
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
    }
  };

  const handleStart = (campaignId) => {
    setCampaigns(campaigns.map(c => 
      c.id === campaignId ? { ...c, statut: 'en_cours' } : c
    ));
  };

  const handlePause = (campaignId) => {
    setCampaigns(campaigns.map(c => 
      c.id === campaignId ? { ...c, statut: 'brouillon' } : c
    ));
  };

  const handleDuplicate = (campaign) => {
    const newCampaign = {
      ...campaign,
      id: Date.now(),
      nom: `${campaign.nom} (Copie)`,
      statut: 'brouillon',
      date_creation: new Date().toISOString().split('T')[0],
      total_envoyes: 0,
      total_livres: 0,
      taux_livraison: 0
    };
    setCampaigns([...campaigns, newCampaign]);
  };

  const handleSaveCampaign = (campaignData) => {
    if (editingCampaign) {
      setCampaigns(campaigns.map(c => 
        c.id === editingCampaign.id ? { ...c, ...campaignData } : c
      ));
    } else {
      const newCampaign = {
        ...campaignData,
        id: Date.now(),
        date_creation: new Date().toISOString().split('T')[0],
        total_envoyes: 0,
        total_livres: 0,
        taux_livraison: 0
      };
      setCampaigns([...campaigns, newCampaign]);
    }
    setIsModalOpen(false);
    setEditingCampaign(null);
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Campagnes SMS</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Créez et gérez vos campagnes de SMS
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setIsModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle campagne
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Rechercher une campagne..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="brouillon">Brouillon</option>
              <option value="en_cours">En cours</option>
              <option value="terminé">Terminé</option>
              <option value="annulé">Annulé</option>
            </select>
            <button className="btn btn-secondary">
              <Filter className="h-4 w-4 mr-2" />
              Filtres
            </button>
          </div>
        </div>
      </div>

      {/* Liste des campagnes */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {filteredCampaigns.map((campaign) => (
          <div key={campaign.id} className="card hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-secondary-900">
                  {campaign.nom}
                </h3>
                <p className="mt-1 text-sm text-secondary-500 line-clamp-2">
                  {campaign.message}
                </p>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.statut)}`}>
                {getStatusLabel(campaign.statut)}
              </span>
            </div>

            <div className="space-y-2 text-sm text-secondary-600">
              <div className="flex justify-between">
                <span>Type d'envoi:</span>
                <span className="capitalize">{campaign.type_envoi}</span>
              </div>
              {campaign.date_planifiee && (
                <div className="flex justify-between">
                  <span>Date planifiée:</span>
                  <span>{new Date(campaign.date_planifiee).toLocaleString('fr-FR')}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Envoyés:</span>
                <span>{campaign.total_envoyes.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Livrés:</span>
                <span>{campaign.total_livres.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Taux de livraison:</span>
                <span className="font-medium">{campaign.taux_livraison}%</span>
              </div>
            </div>

            <div className="mt-4 flex space-x-2">
              {campaign.statut === 'brouillon' && (
                <button
                  onClick={() => handleStart(campaign.id)}
                  className="btn btn-success flex-1 text-sm"
                >
                  <Play className="h-4 w-4 mr-1" />
                  Démarrer
                </button>
              )}
              {campaign.statut === 'en_cours' && (
                <button
                  onClick={() => handlePause(campaign.id)}
                  className="btn btn-secondary flex-1 text-sm"
                >
                  <Pause className="h-4 w-4 mr-1" />
                  Pause
                </button>
              )}
              <button
                onClick={() => handleEdit(campaign)}
                className="btn btn-secondary text-sm"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDuplicate(campaign)}
                className="btn btn-secondary text-sm"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(campaign.id)}
                className="btn btn-danger text-sm"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCampaigns.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-secondary-400" />
          <h3 className="mt-2 text-sm font-medium text-secondary-900">Aucune campagne</h3>
          <p className="mt-1 text-sm text-secondary-500">
            Commencez par créer votre première campagne SMS.
          </p>
          <div className="mt-6">
            <button 
              className="btn btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle campagne
            </button>
          </div>
        </div>
      )}

      {/* Modal de campagne */}
      <CampaignModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCampaign(null);
        }}
        onSave={handleSaveCampaign}
        campaign={editingCampaign}
      />
    </div>
  );
};

export default Campaigns;
